import { useMemo, useState } from 'react';
import { api, can, getUser } from '../lib/api';
import { day, dayTime, number, shortList } from '../lib/format';
import { Card, Empty, ErrorNote, Field, Hash, Loading, Modal, PageHead, Pill, useResource, useToast } from '../components/ui';
import { RowActions } from '../components/RowActions';
import '../styles/connectors.css';

/**
 * Connectors: consent state pushed to the customer's CRM and campaign tools.
 * One table of connectors, one of deliveries. Secrets are entered here and
 * never read back; the API returns a masked hint only.
 */

type TargetKind = 'field' | 'subscription' | 'audience' | 'tag' | 'attribute' | 'request';

interface FieldSpec {
  key: string; label: string; type: 'text' | 'url' | 'secret' | 'select' | 'json' | 'headers';
  required?: boolean; default?: string; options?: Array<[string, string]>; placeholder?: string; help?: string; advanced?: boolean;
}
interface SystemSpec {
  kind: string; label: string; category: 'CRM' | 'Campaign tool' | 'Other'; auth: string;
  fields: FieldSpec[]; targets: Array<{ kind: TargetKind; label: string; placeholder: string; identifierTypes?: string[] }>;
  identifierTypes: Array<'email' | 'phone'>; optOutFields?: string[];
}
interface MapEntry { purposeId: string; kind: TargetKind; target: string; invert?: boolean; valueGranted?: string; valueWithdrawn?: string }
interface Connector {
  id: string; kind: string; system: string; name: string; status: 'active' | 'paused' | 'error'; hasCredentials: boolean;
  identifierType: 'email' | 'phone'; config: Record<string, unknown>; secrets: Record<string, string>; summary: string;
  purposeMap: Array<MapEntry & { purposeKey: string | null; purposeName: string | null }>;
  lastOkAt: string | null; lastError: string | null; lastTestAt: string | null; lastTestOk: boolean | null; lastTestNote: string | null;
  counts: { delivered7d: number; pending: number; dead: number; notFound7d: number; skipped7d: number };
  lastDelivery: { at: string; status: string } | null;
}
interface Delivery {
  id: string; connector_id: string; connector_name: string; principal_hash: string; purpose_name: string | null; purpose_key: string | null;
  action: string; status: string; attempts: number; response_code: number | null; note: string | null; error: string | null;
  next_attempt_at: string | null; delivered_at: string | null; created_at: string; updated_at: string;
}
interface Purpose { id: string; key: string; status: string; current: { name: string } }

const ID_LABEL = { email: 'Email', phone: 'Mobile number' } as const;
const ACTION_LABEL: Record<string, string> = { granted: 'Granted', refreshed: 'Renewed', withdrawn: 'Withdrawn', declined: 'Declined', expired: 'Expired' };
const DELIVERY: Record<string, { label: string; tone?: 'ok' | 'warn' | 'bad' }> = {
  pending: { label: 'Waiting', tone: 'warn' },
  retrying: { label: 'Retrying', tone: 'warn' },
  delivered: { label: 'Delivered', tone: 'ok' },
  not_found: { label: 'Not in system', tone: 'warn' },
  skipped: { label: 'Skipped' },
  dead: { label: 'Failed', tone: 'bad' },
};
const FILTERS: Array<[string, string]> = [['', 'All'], ['pending', 'Waiting'], ['delivered', 'Delivered'], ['not_found', 'Not in system'], ['skipped', 'Skipped'], ['dead', 'Failed']];

function statusOf(c: Connector): { label: string; tone?: 'ok' | 'warn' | 'bad' } {
  if (!c.hasCredentials) return { label: 'No credentials', tone: 'warn' };
  if (c.status === 'paused') return { label: 'Paused' };
  if (c.status === 'error' || c.counts.dead > 0) return { label: 'Failing', tone: 'bad' };
  if (c.lastError && c.counts.pending > 0) return { label: 'Retrying', tone: 'warn' };
  return { label: 'Active', tone: 'ok' };
}

export default function Connectors() {
  const toast = useToast();
  const writable = can(getUser(), 'processors:edit');
  const [nonce, setNonce] = useState(0);
  const [view, setView] = useState<'connectors' | 'deliveries'>('connectors');
  const [filter, setFilter] = useState('');
  const [only, setOnly] = useState('');
  const [editing, setEditing] = useState<Connector | 'new' | null>(null);

  const list = useResource<{ connectors: Connector[] }>(() => api.get('/v1/connectors'), [nonce]);
  const sys = useResource<{ systems: SystemSpec[] }>(() => api.get('/v1/connectors/systems'), []);
  const purposes = useResource<{ purposes: Purpose[] }>(() => api.get('/v1/purposes'), []);
  const dl = useResource<{ deliveries: Delivery[] }>(
    () => api.get(`/v1/connectors/deliveries?limit=200${filter ? `&status=${filter}` : ''}${only ? `&connectorId=${only}` : ''}`),
    [nonce, filter, only],
  );

  const rows = list.data?.connectors ?? [];
  const delivered = rows.reduce((n, c) => n + c.counts.delivered7d, 0);
  const waiting = rows.reduce((n, c) => n + c.counts.pending, 0);
  const failed = rows.reduce((n, c) => n + c.counts.dead, 0);

  async function act(fn: () => Promise<unknown>, done: string) {
    try {
      await fn();
      toast(done, 'ok');
      setNonce((n) => n + 1);
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  async function test(c: Connector) {
    try {
      const r = await api.post<{ ok: boolean; detail: string }>(`/v1/connectors/${c.id}/test`);
      toast(r.detail, r.ok ? 'ok' : 'bad');
      setNonce((n) => n + 1);
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  return (
    <>
      <PageHead title="Connectors">
        {writable && rows.length > 0 && (
          <button onClick={() => act(async () => {
            const r = await api.post<{ delivered: number; failed: number }>('/v1/connectors/run');
            toast(`${r.delivered} delivered, ${r.failed} failed.`, r.failed ? 'bad' : 'ok');
          }, 'Run complete.')}>Run now</button>
        )}
        {writable && <button className="primary" onClick={() => setEditing('new')}>Add connector</button>}
      </PageHead>
      <div className="cn">

      <p className="page-line">
        <strong>{number(rows.length)}</strong> connector{rows.length === 1 ? '' : 's'} · {number(delivered)} delivered in 7 days · {number(waiting)} waiting · {number(failed)} failed
      </p>

      <div className="q-bar">
        <div className="q-views" role="tablist">
          <button className={view === 'connectors' ? 'on' : ''} onClick={() => setView('connectors')}>Connectors<span className="n">{rows.length}</span></button>
          <button className={view === 'deliveries' ? 'on' : ''} onClick={() => setView('deliveries')}>Deliveries{failed ? <span className="n">{failed} failed</span> : null}</button>
        </div>
        {view === 'deliveries' && (
          <div className="cn-filters">
            <select value={only} onChange={(e) => setOnly(e.target.value)} aria-label="Connector">
              <option value="">All connectors</option>
              {rows.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Status">
              {FILTERS.map(([k, l]) => <option key={k || 'all'} value={k}>{k ? l : 'All statuses'}</option>)}
            </select>
          </div>
        )}
      </div>

      <ErrorNote error={list.error} />

      {view === 'connectors' && (
        list.loading && !list.data ? <Loading what="Loading connectors" /> : rows.length === 0 ? <Card><Empty>No connectors.</Empty></Card> : (
          <Card>
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>System</th>
                    <th>Purposes mapped</th>
                    <th>Identifier</th>
                    <th>Last delivery</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => {
                    const st = statusOf(c);
                    const names = [...new Set(c.purposeMap.map((m) => m.purposeName ?? m.purposeKey ?? ''))].filter(Boolean);
                    return (
                      <tr key={c.id}>
                        <td title={c.summary}><strong>{c.name}</strong></td>
                        <td>{c.system}</td>
                        <td className="wrap">{shortList(names, 2)}</td>
                        <td>{ID_LABEL[c.identifierType]}</td>
                        <td>{c.lastDelivery ? day(c.lastDelivery.at) : '—'}</td>
                        <td title={st.tone === 'bad' ? c.lastError ?? '' : ''}><Pill tone={st.tone}>{st.label}</Pill></td>
                        <td style={{ textAlign: 'right' }}>
                          {writable && (
                            <RowActions
                              primary={<button className="small" onClick={() => setEditing(c)}>Edit</button>}
                              more={[
                                { label: 'Test connection', onClick: () => test(c), disabled: !c.hasCredentials },
                                { label: 'View deliveries', onClick: () => { setOnly(c.id); setView('deliveries'); } },
                                { label: 'Send current state', onClick: () => {
                                  if (!window.confirm(`Send the current decision of every person for the mapped purposes to ${c.name}?`)) return;
                                  act(async () => {
                                    const r = await api.post<{ queued: number }>(`/v1/connectors/${c.id}/send-current-state`);
                                    toast(`${number(r.queued)} queued.`, 'ok');
                                  }, 'Queued.');
                                } },
                                c.status === 'paused'
                                  ? { label: 'Resume', onClick: () => act(() => api.post(`/v1/connectors/${c.id}/status`, { status: 'active' }), 'Resumed.'), disabled: !c.hasCredentials }
                                  : { label: 'Pause', onClick: () => act(() => api.post(`/v1/connectors/${c.id}/status`, { status: 'paused' }), 'Paused.') },
                                { label: 'Erase credentials', danger: true, disabled: !c.hasCredentials, onClick: () => {
                                  if (window.confirm(`Erase the stored credentials for ${c.name}? Deliveries wait until new credentials are entered.`)) {
                                    act(() => api.delete(`/v1/connectors/${c.id}/credentials`), 'Credentials erased.');
                                  }
                                } },
                                { label: 'Delete connector', danger: true, onClick: () => {
                                  if (window.confirm(`Delete ${c.name}? Its delivery record is kept.`)) act(() => api.delete(`/v1/connectors/${c.id}`), 'Deleted.');
                                } },
                              ]}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )
      )}

      {view === 'deliveries' && (
        <>
          <ErrorNote error={dl.error} />
          {dl.loading && !dl.data ? <Loading what="Loading deliveries" /> : (dl.data?.deliveries.length ?? 0) === 0 ? <Card><Empty>No deliveries.</Empty></Card> : (
            <Card>
              <div className="table-scroll">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Recorded</th>
                      <th>Connector</th>
                      <th>Purpose</th>
                      <th>Change</th>
                      <th>Person</th>
                      <th>Status</th>
                      <th>Reply</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {dl.data!.deliveries.map((d) => {
                      const st = DELIVERY[d.status] ?? { label: d.status };
                      const reply = d.error ?? d.note ?? '';
                      return (
                        <tr key={d.id}>
                          <td>{dayTime(d.created_at)}</td>
                          <td>{d.connector_name}</td>
                          <td>{d.purpose_name ?? d.purpose_key ?? '—'}</td>
                          <td>{ACTION_LABEL[d.action] ?? d.action}</td>
                          <td><Hash value={d.principal_hash} /></td>
                          <td><Pill tone={st.tone}>{st.label}</Pill></td>
                          <td className="reply"><div className="cn-reply" title={reply}>{d.attempts > 1 ? <span className="cn-code">Attempt {d.attempts}</span> : null}{d.response_code ? <span className="cn-code">{d.response_code}</span> : null}{reply || (d.next_attempt_at && d.status !== 'pending' ? `Next ${dayTime(d.next_attempt_at)}` : '')}</div></td>
                          <td style={{ textAlign: 'right' }}>
                            {writable && ['retrying', 'dead', 'not_found', 'skipped'].includes(d.status) && (
                              <button className="small" onClick={() => act(() => api.post(`/v1/connectors/deliveries/${d.id}/retry`), 'Queued for retry.')}>Retry</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {editing && sys.data && (
        <ConnectorDialog
          systems={sys.data.systems}
          purposes={purposes.data?.purposes ?? []}
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setNonce((n) => n + 1); }}
        />
      )}
      </div>
    </>
  );
}

// ------------------------------------------------------------------ dialog

interface Row { key: string; purposeId: string; kind: TargetKind | ''; target: string; whenYes: string; whenNo: string }

function headersToText(h: unknown): string {
  if (!h || typeof h !== 'object') return '';
  return Object.entries(h as Record<string, string>).map(([k, v]) => `${k}: ${v}`).join('\n');
}

function textToHeaders(t: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of t.split('\n')) {
    const i = line.indexOf(':');
    if (i > 0) out[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  }
  return out;
}

const OUTCOME: Record<string, [string, string]> = {
  subscription: ['Subscribed', 'Unsubscribed'],
  audience: ['Subscribed', 'Unsubscribed'],
  tag: ['Tag active', 'Tag inactive'],
  request: ['true', 'false'],
};

let rowSeq = 0;

function ConnectorDialog({ systems, purposes, existing, onClose, onSaved }: {
  systems: SystemSpec[]; purposes: Purpose[]; existing: Connector | null; onClose: () => void; onSaved: () => void;
}) {
  const toast = useToast();
  const [kind, setKind] = useState(existing?.kind ?? '');
  const spec = systems.find((s) => s.kind === kind) ?? null;
  const [name, setName] = useState(existing?.name ?? '');
  const [identifierType, setIdentifierType] = useState<'email' | 'phone'>(existing?.identifierType ?? 'email');
  const [values, setValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = {};
    for (const [k, val] of Object.entries(existing?.config ?? {})) v[k] = k === 'headers' ? headersToText(val) : String(val ?? '');
    return v;
  });
  const [rows, setRows] = useState<Row[]>(() => initialRows(purposes, existing));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; detail: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  function initialRows(ps: Purpose[], ex: Connector | null): Row[] {
    const out: Row[] = [];
    for (const p of ps) {
      const mapped = (ex?.purposeMap ?? []).filter((m) => m.purposeId === p.id);
      // Active purposes, plus any purpose already mapped, so saving never drops a mapping.
      if (p.status !== 'active' && !mapped.length) continue;
      if (!mapped.length) out.push({ key: `r${rowSeq++}`, purposeId: p.id, kind: '', target: '', whenYes: '', whenNo: '' });
      for (const m of mapped) {
        out.push({
          key: `r${rowSeq++}`, purposeId: p.id, kind: m.kind, target: m.target,
          whenYes: m.valueGranted ?? (m.invert ? 'false' : ''), whenNo: m.valueWithdrawn ?? (m.invert ? 'true' : ''),
        });
      }
    }
    return out;
  }

  const setVal = (k: string, v: string) => { setValues((s) => ({ ...s, [k]: v })); setResult(null); };
  const setRow = (key: string, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const grouped = useMemo(() => (['CRM', 'Campaign tool', 'Other'] as const).map((c) => [c, systems.filter((s) => s.category === c)] as const), [systems]);

  function configPayload(includeSecrets: boolean): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const f of spec?.fields ?? []) {
      const v = values[f.key] ?? '';
      if (f.type === 'secret') { if (includeSecrets && v) out[f.key] = v; continue; }
      if (f.type === 'headers') { if (v.trim()) out[f.key] = textToHeaders(v); continue; }
      if (v !== '') out[f.key] = v;
    }
    return out;
  }

  function mapPayload(): MapEntry[] {
    return rows.filter((r) => r.kind && r.target.trim()).map((r) => {
      const e: MapEntry = { purposeId: r.purposeId, kind: r.kind as TargetKind, target: r.target.trim() };
      if (r.kind === 'field' || r.kind === 'attribute') {
        const y = r.whenYes.trim(); const n = r.whenNo.trim();
        if (y === 'false' && (n === 'true' || n === '')) e.invert = true;
        else if ((y === 'true' || y === '') && (n === 'false' || n === '')) { if (y || n) e.invert = false; }
        else { if (y) e.valueGranted = y; if (n) e.valueWithdrawn = n; }
      }
      return e;
    });
  }

  function secretsPayload(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const f of spec?.fields ?? []) if (f.type === 'secret' && values[f.key]) out[f.key] = values[f.key]!;
    return out;
  }

  async function runTest() {
    if (!spec) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await api.post<{ ok: boolean; detail: string }>('/v1/connectors/test', {
        kind: spec.kind, config: configPayload(true), ...(existing ? { connectorId: existing.id } : {}),
      });
      setResult(r);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!spec) return;
    setBusy(true); setError(null);
    try {
      if (existing) {
        await api.patch(`/v1/connectors/${existing.id}`, existing.hasCredentials
          ? { name, identifierType, config: configPayload(false), purposeMap: mapPayload() }
          : { name, identifierType, purposeMap: mapPayload() });
        const secrets = secretsPayload();
        if (Object.keys(secrets).length || !existing.hasCredentials) {
          await api.put(`/v1/connectors/${existing.id}/credentials`, { ...(existing.hasCredentials ? {} : configPayload(false)), ...secrets });
        }
        toast('Connector saved.', 'ok');
      } else {
        await api.post('/v1/connectors', { kind: spec.kind, name, identifierType, config: configPayload(true), purposeMap: mapPayload() });
        toast('Connector added.', 'ok');
      }
      onSaved();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  const basic = spec?.fields.filter((f) => !f.advanced) ?? [];
  const advanced = spec?.fields.filter((f) => f.advanced) ?? [];
  const targets = (spec?.targets ?? []).filter((t) => !t.identifierTypes || t.identifierTypes.includes(identifierType));
  const missingRequired = (spec?.fields ?? []).some((f) => f.required && !values[f.key] && !(f.type === 'secret' && existing?.secrets[f.key]));

  const field = (f: FieldSpec) => {
    const v = values[f.key] ?? '';
    const hint = existing?.secrets[f.key];
    let input;
    if (f.type === 'secret') {
      input = <input type="password" autoComplete="new-password" value={v} placeholder={hint ? `${hint} (unchanged)` : f.placeholder ?? ''} onChange={(e) => setVal(f.key, e.target.value)} />;
    } else if (f.type === 'select') {
      input = <select value={v || f.default || ''} onChange={(e) => setVal(f.key, e.target.value)}>{(f.options ?? []).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>;
    } else if (f.type === 'json' || f.type === 'headers') {
      input = <textarea className="mono" rows={f.type === 'json' ? 8 : 3} value={v || (f.type === 'json' ? f.default ?? '' : '')} onChange={(e) => setVal(f.key, e.target.value)} placeholder={f.type === 'headers' ? 'X-Source: consent' : ''} />;
    } else {
      input = <input type={f.type === 'url' ? 'url' : 'text'} value={v} placeholder={f.placeholder ?? f.default ?? ''} onChange={(e) => setVal(f.key, e.target.value)} />;
    }
    const wide = f.type === 'json' || f.type === 'headers' || f.type === 'url';
    return (
      <div key={f.key} className={`field${wide ? ' span' : ''}`}>
        <label>{f.label}{f.required || f.type === 'select' || f.default ? '' : ' (optional)'}</label>
        {input}
        {f.help && <div className="help">{f.help}</div>}
      </div>
    );
  };

  return (
    <Modal
      wide
      title={existing ? `Edit ${existing.name}` : 'Add connector'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={busy}>Cancel</button>
          <button onClick={runTest} disabled={busy || !spec || missingRequired}>Test connection</button>
          <button className="primary" onClick={save} disabled={busy || !spec || !name.trim() || missingRequired}>{busy ? 'Saving…' : 'Save'}</button>
        </>
      }
    >
      <div className="cn-form">
        <div className="cn-grid">
          <Field label="System">
            {existing ? <input type="text" value={existing.system} disabled /> : (
              <select value={kind} onChange={(e) => { setKind(e.target.value); setValues({}); setResult(null); const s = systems.find((x) => x.kind === e.target.value); if (s && !s.identifierTypes.includes(identifierType)) setIdentifierType(s.identifierTypes[0]!); setRows((rs) => rs.map((r) => ({ ...r, kind: '', target: '' }))); }}>
                <option value="">Choose system</option>
                {grouped.map(([cat, list]) => (
                  <optgroup key={cat} label={cat}>{list.map((s) => <option key={s.kind} value={s.kind}>{s.label}</option>)}</optgroup>
                ))}
              </select>
            )}
          </Field>
          <Field label="Name"><input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={spec ? `${spec.label} production` : ''} /></Field>
          {spec && (
            <Field label="Match people on" help={`Authentication: ${spec.auth}.`}>
              <select value={identifierType} onChange={(e) => setIdentifierType(e.target.value as 'email' | 'phone')}>
                {spec.identifierTypes.map((t) => <option key={t} value={t}>{ID_LABEL[t]}</option>)}
              </select>
            </Field>
          )}
        </div>

        {spec && (
          <>
            <div className="cn-section">Connection</div>
            <div className="cn-grid">{basic.map(field)}</div>
            {advanced.length > 0 && (
              <details className="cn-adv">
                <summary>Advanced</summary>
                <div className="cn-grid">{advanced.map(field)}</div>
              </details>
            )}

            <div className="cn-section">Purposes</div>
            {rows.length === 0 ? <Empty>No active purposes.</Empty> : (
              <div className="table-scroll">
                <table className="cn-map">
                  <thead>
                    <tr><th>Purpose</th><th>Sent to</th><th>Name in {spec.label}</th><th>Consent</th><th>No consent</th><th /></tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const p = purposes.find((x) => x.id === r.purposeId);
                      const first = i === 0 || rows[i - 1]!.purposeId !== r.purposeId;
                      const t = targets.find((x) => x.kind === r.kind);
                      const valued = r.kind === 'field' || r.kind === 'attribute';
                      const optOut = valued && (spec.optOutFields ?? []).some((f) => f.toLowerCase() === r.target.trim().toLowerCase());
                      return (
                        <tr key={r.key}>
                          <td className="p">{first ? p?.current.name ?? p?.key : ''}</td>
                          <td className="kind">
                            <select value={r.kind} onChange={(e) => setRow(r.key, { kind: e.target.value as TargetKind | '' })}>
                              <option value="">Not sent</option>
                              {targets.map((x) => <option key={x.kind} value={x.kind}>{x.label}</option>)}
                            </select>
                          </td>
                          <td><input type="text" value={r.target} disabled={!r.kind} placeholder={t?.placeholder ?? ''} onChange={(e) => setRow(r.key, { target: e.target.value })} /></td>
                          <td className="val">{valued
                            ? <input type="text" value={r.whenYes} placeholder={optOut ? 'false' : 'true'} onChange={(e) => setRow(r.key, { whenYes: e.target.value })} />
                            : <span className="muted">{r.kind ? OUTCOME[r.kind]?.[0] : ''}</span>}</td>
                          <td className="val">{valued
                            ? <input type="text" value={r.whenNo} placeholder={optOut ? 'true' : 'false'} onChange={(e) => setRow(r.key, { whenNo: e.target.value })} />
                            : <span className="muted">{r.kind ? OUTCOME[r.kind]?.[1] : ''}</span>}</td>
                          <td className="x">
                            {first
                              ? <button className="small" disabled={!r.kind} onClick={() => setRows((rs) => { const at = rs.findIndex((x) => x.key === r.key); const copy = [...rs]; copy.splice(at + 1, 0, { key: `r${rowSeq++}`, purposeId: r.purposeId, kind: r.kind, target: '', whenYes: '', whenNo: '' }); return copy; })}>Add</button>
                              : <button className="small" onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}>Remove</button>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {result && <div className={`note ${result.ok ? 'ok' : 'bad'} cn-result`} role="status">{result.ok ? 'Connection test passed. ' : 'Connection test failed. '}{result.detail}</div>}
        <ErrorNote error={error} />
      </div>
    </Modal>
  );
}
